/**
 * API client for the Enereg backend.
 * All data fetching goes through these functions.
 * Supports JWT auth (Supabase Auth) and session cookie auth (Bun server).
 */

const BASE = "/api";

// Token storage — in memory + sessionStorage (tab-scoped, cleared on close)
// Uses sessionStorage instead of localStorage to reduce XSS token theft window
let _accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  _accessToken = token;
  if (token) {
    sessionStorage.setItem("enereg_token", token);
  } else {
    sessionStorage.removeItem("enereg_token");
  }
  // Clean up any legacy localStorage tokens
  try { localStorage.removeItem("enereg_token"); } catch {}
}

export function getAccessToken(): string | null {
  if (!_accessToken) {
    _accessToken = sessionStorage.getItem("enereg_token");
  }
  return _accessToken;
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${url}`, {
    headers,
    ...options,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `API error: ${res.status}`);
  }
  return res.json();
}

/** Where the checkup token is parked so it survives the SSO round-trip (which drops query params).
 *  A completed checkup IS an invite — see auth.ts checkAdmission — and the SAME token is the
 *  pre-fill capability the onboarding wizard presents to /api/checkup-snapshot. Two consumers,
 *  one stash, different lifetimes:
 *
 *  - The INVITE is spent at session exchange (takeCheckupToken → markCheckupTokenRedeemed).
 *    Spending must not delete the stash: on the magic-link path the email opens a FRESH TAB,
 *    where the sessionStorage redirect and the URL query params are both already gone, so this
 *    stash is the only carrier the wizard has left. Deleting it at redemption is exactly what
 *    used to leave the wizard silently blank. The `redeemed` mark keeps sign-in replay-proof
 *    instead: takeCheckupToken never hands a redeemed token to the exchange again.
 *  - The PRE-FILL capability (peekCheckupToken) lives on until the wizard creates a project
 *    (clearCheckupToken) or the TTL lapses.
 *
 *  localStorage, NOT sessionStorage: a fresh tab starts with empty sessionStorage, so a
 *  session-scoped stash would vanish and dead-end the user at "enter a code." */
export const CHECKUP_TOKEN_KEY = "enereg_checkup_token";
export const CHECKUP_TOKEN_TTL_MS = 60 * 60 * 1000; // 1 hour — a signup should follow a checkup promptly

export type CheckupStash = { token: string; at: number; redeemed: boolean };

/** Pure parse + TTL gate over the raw stash record, so every expiry/corruption case is
 *  testable without a DOM (this repo deliberately has no jsdom/happy-dom). */
export function parseCheckupStash(raw: string | null, nowMs: number): CheckupStash | null {
  if (!raw) return null;
  try {
    const { token, at, redeemed } = JSON.parse(raw);
    if (typeof token !== "string" || !token || typeof at !== "number" || nowMs - at > CHECKUP_TOKEN_TTL_MS) return null;
    return { token, at, redeemed: redeemed === true };
  } catch { return null; }
}

function readCheckupStash(): CheckupStash | null {
  try {
    const stash = parseCheckupStash(localStorage.getItem(CHECKUP_TOKEN_KEY), Date.now());
    if (!stash) localStorage.removeItem(CHECKUP_TOKEN_KEY); // expired/corrupt — don't let it linger
    return stash;
  } catch { return null; }
}

export function stashCheckupToken(token: string): void {
  try {
    // Re-stashing the SAME token (revisiting the CTA link, a login flash) must not un-spend
    // a redeemed invite — that re-arms a token the server has already refused once and feeds
    // a 403-replay loop. A NEW token is a new checkup: fresh, unredeemed record.
    const cur = parseCheckupStash(localStorage.getItem(CHECKUP_TOKEN_KEY), Date.now());
    const redeemed = cur?.token === token ? cur.redeemed : false;
    localStorage.setItem(CHECKUP_TOKEN_KEY, JSON.stringify({ token, at: Date.now(), redeemed }));
  } catch {}
}

/** The invite consumer (session exchange). A redeemed token is never handed out again. */
export function takeCheckupToken(): string | undefined {
  const stash = readCheckupStash();
  return stash && !stash.redeemed ? stash.token : undefined;
}

/** Spend the invite WITHOUT destroying the pre-fill capability. Marks only the given token:
 *  the exchange awaited the network, and a NEWER checkup may have been stashed meanwhile —
 *  marking blindly would spend an invite that was never presented to the server. */
export function markCheckupTokenRedeemed(token: string): void {
  const stash = readCheckupStash();
  if (!stash || stash.token !== token) return;
  try { localStorage.setItem(CHECKUP_TOKEN_KEY, JSON.stringify({ ...stash, redeemed: true })); } catch {}
}

/** The pre-fill consumer (post-auth redirect + onboarding wizard). A redeemed stash is fine
 *  here — redeeming the invite is precisely what happens right before the wizard needs it. */
export function peekCheckupToken(): string | undefined {
  return readCheckupStash()?.token;
}

export function clearCheckupToken(): void {
  try { localStorage.removeItem(CHECKUP_TOKEN_KEY); } catch {}
}

/** The onboarding-wizard URL that carries the checkup pre-fill capability. */
export function checkupWizardPath(token?: string): string {
  return `/projects/new?from=checkup${token ? `&checkup_token=${encodeURIComponent(token)}` : ""}`;
}

/** Post-auth landing. The sessionStorage redirect is the primary carrier, but it only survives
 *  same-tab flows (OAuth full-page redirect). On the magic-link hop it is gone — send the user
 *  to the wizard WITHOUT re-materializing the token into the URL (browser history is no place
 *  for a bearer capability); the wizard's own stash fallback carries it the last step. */
export function resolvePostAuthRedirect(storedRedirect: string | null, hasStashedToken: boolean): string {
  if (storedRedirect) return storedRedirect;
  if (hasStashedToken) return checkupWizardPath();
  return "/projects";
}

/** The post-auth redirect key (sessionStorage — tab-scoped BY DESIGN: it describes this tab's
 *  pending sign-in; the localStorage checkup stash covers the cross-tab hop). The write half
 *  runs in LoginPage, the read half in AuthCallback — the key and both wrappers live here so
 *  the pair can't drift. */
export const POST_AUTH_REDIRECT_KEY = "enereg_post_auth_redirect";

export function stashPostAuthRedirect(path: string): void {
  try { sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path); } catch {}
}

export function clearPostAuthRedirect(): void {
  try { sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY); } catch {}
}

/** One-shot: reading the redirect consumes it. */
export function consumePostAuthRedirect(): string {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  } catch {}
  return resolvePostAuthRedirect(stored, peekCheckupToken() !== undefined);
}

/** Which checkup capability the wizard should use: the URL wins (fresh CTA link, same-tab
 *  OAuth), the stash covers every path where the URL lost its params — the magic-link new
 *  tab, or a plain "Create New Project" click within the TTL. */
export function resolveCheckupPrefill(
  urlFrom: string | null,
  urlToken: string | null,
  stashedToken: string | undefined,
): { fromCheckup: boolean; checkupToken: string | undefined } {
  const checkupToken = urlToken || stashedToken || undefined;
  const fromCheckup = urlFrom === "checkup" || (!urlToken && !!stashedToken);
  return { fromCheckup, checkupToken };
}

// Auth
export const auth = {
  signUp: (data: {
    email: string; password: string; name: string; org_name?: string;
    beta_code?: string; checkup_token?: string;
  }) =>
    fetchJSON<{ user: any }>("/auth/signup", { method: "POST", body: JSON.stringify(data) }),

  logIn: (data: { email: string; password: string }) =>
    fetchJSON<{ user: any; access_token?: string; refresh_token?: string }>("/auth/login", { method: "POST", body: JSON.stringify(data) }),

  logOut: () => fetchJSON<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  getMe: () => fetchJSON<{ user: any | null; beta_required?: boolean }>("/auth/me"),

  // Exchange the current Supabase Bearer token (set via setAccessToken) for a first-party HttpOnly
  // session cookie, so /auth/me and browser-initiated asset loads authenticate for SSO users (#15).
  // A first-time SSO user needs a ticket: an invite code, OR a checkup token (a completed checkup
  // admits you). With neither, the server returns { needs_invite: true } and the UI asks for a code.
  exchangeSession: (inviteCode?: string, checkupToken?: string) =>
    fetchJSON<{ user?: any; needs_invite?: boolean; email?: string }>("/auth/session", {
      method: "POST",
      body: JSON.stringify({
        ...(inviteCode ? { invite_code: inviteCode } : {}),
        ...(checkupToken ? { checkup_token: checkupToken } : {}),
      }),
    }),

  verifyBeta: (code: string) =>
    fetchJSON<{ ok: boolean }>("/auth/verify-beta", { method: "POST", body: JSON.stringify({ beta_code: code }) }),

  // GDPR data portability — a machine-readable copy of everything we hold about the caller.
  exportMyData: () => fetchJSON<Record<string, any>>("/account/export"),

  // GDPR erasure — permanently delete the caller's account and all personal data. Irreversible.
  deleteAccount: () => fetchJSON<{ ok: boolean }>("/account", { method: "DELETE" }),
};

// The project-profile attributes the wizard collects (mirrors ProjectAttributeInput server-side).
export interface ProjectAttributesPayload {
  interconnection_mode?: string | null;
  imports_generator?: boolean | null;
  imports_equipment?: boolean | null;
  hybrid_or_fuel_fired?: boolean | null;
  has_battery_storage?: boolean | null;
  local_assembly_or_manufacture?: boolean | null;
  employs_expatriates?: boolean | null;
  site_flags?: string[] | null;
  programme_participation?: string[] | null;
  state_region?: string | null;
}

// What /api/projects/scope-preview answers with — the wizard's Scope tiles.
// required/conditional/under_review partition the BASELINE (day-one) rows only;
// elective/lifecycle/third-party are real but counted apart so they cannot
// inflate the headline (same doctrine as the checkup's EvaluationOutput).
export interface ScopePreview {
  licenses_required: number;
  licenses_conditional: number;
  licenses_under_review: number;
  licenses_elective: number;
  licenses_lifecycle: number;
  licenses_third_party: number;
  licenses_in_scope: number;
  licenses_total: number;
  fields_total: number;
  /** Statutory critical path in months; null when no timeline data is in scope. */
  est_months: number | null;
}

// Projects
export const api = {
  getProjects: () => fetchJSON<any[]>("/projects"),

  getProject: (id: string) => fetchJSON<any>(`/projects/${id}`),

  renameProject: (id: string, name: string) =>
    fetchJSON<{ ok: boolean; name: string }>(`/projects/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),

  deleteProject: (id: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${id}`, { method: "DELETE" }),

  createProject: (data: {
    name: string; country_code: string; capacity_kw: number; energy_type?: string; intent?: string;
    attributes?: ProjectAttributesPayload;
  }) =>
    fetchJSON<{ projectId: string; licensesCreated: number; licensesTotal: number }>("/projects", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Dry-run of createProject's licence routing for the wizard's Scope screen — no writes.
  scopePreview: (data: {
    country_code: string; capacity_kw: number; intent?: string;
    attributes?: ProjectAttributesPayload;
  }) =>
    fetchJSON<ScopePreview>("/projects/scope-preview", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getProjectFields: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/fields`),

  updateProjectField: (projectId: string, fieldId: string, value: string | null) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/fields`, {
      method: "PATCH",
      body: JSON.stringify({ field_id: fieldId, value }),
    }),

  getProjectLicenses: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/licenses`),

  getProjectDocuments: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/documents`),

  getProjectAlerts: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/alerts`),

  getRegulatoryStatus: (projectId: string) =>
    fetchJSON<{
      status: {
        version: string;
        sha: string;
        applied_at: string;
        diff: { added: string[]; changed: string[]; retired: string[] } | null;
      } | null;
      max_reliance_age_days: number;
    }>(`/projects/${projectId}/regulatory-status`),

  getLicenseTypes: (country: string) => fetchJSON<any[]>(`/license-types/${country}`),

  getFieldDefinitions: (country: string) => fetchJSON<any[]>(`/field-definitions/${country}`),

  // Documents & Extraction
  uploadDocument: async (projectId: string, file: File, docType: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    const res = await fetch(`${BASE}/projects/${projectId}/documents`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error.error || `Upload failed: ${res.status}`);
    }
    return res.json();
  },

  extractDocument: (projectId: string, docId: string) =>
    fetchJSON<any>(`/projects/${projectId}/documents/${docId}/extract`),

  deleteDocument: (projectId: string, docId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/documents/${docId}`, { method: "DELETE" }),

  retryExtraction: (projectId: string, docId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/documents/${docId}/retry`, { method: "POST" }),

  recordExtractionFeedback: (projectId: string, feedback: {
    document_id: string;
    field_id: string;
    extracted_value?: string;
    extracted_confidence?: number;
    action: "confirmed" | "corrected" | "rejected";
    corrected_value?: string;
    doc_type?: string;
    country_code?: string;
  }) => fetchJSON<{ ok: boolean; id: string }>(`/projects/${projectId}/extraction-feedback`, {
    method: "POST",
    body: JSON.stringify(feedback),
  }),

  getProjectCosts: (projectId: string) =>
    fetchJSON<{ total_usd: number; by_operation: Record<string, number>; details: any[] }>(`/projects/${projectId}/costs`),

  askRegulatoryQuestion: (question: string, countryCode: string, context?: string) =>
    fetchJSON<{ answer: string; sources: any[]; is_mock: boolean }>("/regulatory-qa", {
      method: "POST",
      body: JSON.stringify({ question, country_code: countryCode, context }),
    }),

  getRegulatoryStatus: (country: string) =>
    fetchJSON<{ country: string; last_checked_at: string | null; latest_change_at: string | null }>(`/regulatory-status/${country}`),

  classifyDocument: (projectId: string, docId: string) =>
    fetchJSON<{ docType: string | null; confidence: number }>(`/projects/${projectId}/documents/${docId}/classify`),

  updateDocumentType: (projectId: string, docId: string, docType: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/documents/${docId}/type`, {
      method: "PATCH",
      body: JSON.stringify({ doc_type: docType }),
    }),

  batchUpdateFields: (projectId: string, updates: Array<{ field_id: string; value: string; source_document_id?: string; source_page?: number }>) =>
    fetchJSON<{ ok: boolean; updated: number }>(`/projects/${projectId}/fields/batch`, {
      method: "POST",
      body: JSON.stringify({ updates }),
    }),

  getDocumentFileUrl: (projectId: string, docId: string) =>
    `${BASE}/projects/${projectId}/documents/${docId}/file`,

  getDocumentFields: (projectId: string, docId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/documents/${docId}/fields`),

  getDocTypes: () => fetchJSON<any>(`/doc-types`),

  // Templates
  getProjectTemplates: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/templates`),

  getTemplateCompleteness: (projectId: string, templateId: string) =>
    fetchJSON<any>(`/projects/${projectId}/templates/${templateId}`),

  generateTemplate: async (projectId: string, templateId: string) => {
    // Kick off background generation
    const start = await fetchJSON<any>(`/projects/${projectId}/templates/${templateId}/generate`, { method: "POST" });
    if (!start?.generation_id) throw new Error("Failed to start generation");

    // Poll for completion
    const generationId = start.generation_id;
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 2000));
      const status = await fetchJSON<any>(
        `/projects/${projectId}/templates/${templateId}/generate/status?id=${generationId}`
      );
      if (status.status === "complete") return status;
      if (status.status === "error") throw new Error(status.error || "Generation failed");
    }
    throw new Error("Generation timed out");
  },

  downloadTemplate: async (projectId: string, templateId: string) => {
    const res = await fetch(`${BASE}/projects/${projectId}/templates/${templateId}/download`, { method: "POST" });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const filename = res.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] || "template.docx";
    // Trigger browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return { filename };
  },

  getProjectActivity: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/activity`),

  getActivityStreak: (projectId: string) =>
    fetchJSON<{ fields_updated_today: number }>(`/projects/${projectId}/activity-streak`),

  // Evaluation
  getProjectEvaluation: (projectId: string) => fetchJSON<any>(`/projects/${projectId}/evaluation`),

  // Alerts
  dismissAlert: (projectId: string, alertId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/alerts/${alertId}/dismiss`, { method: "POST" }),

  // Compliance
  getProjectCompliance: (projectId: string) =>
    fetchJSON<{ items: any[]; summary: any }>(`/projects/${projectId}/compliance`),

  updateProjectCompliance: (projectId: string, countryComplianceId: string, status: string, evidenceNotes?: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/compliance`, {
      method: "PATCH",
      body: JSON.stringify({ country_compliance_id: countryComplianceId, status, evidence_notes: evidenceNotes }),
    }),

  getCountryCompliance: (country: string, opts?: { pillar?: "ENV" | "SOC"; track?: "government" | "investment" }) => {
    const params = new URLSearchParams();
    if (opts?.pillar) params.set("pillar", opts.pillar);
    if (opts?.track) params.set("track", opts.track);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return fetchJSON<any[]>(`/compliance/${country}${qs}`);
  },

  getComplianceDetail: (id: string) => fetchJSON<any>(`/compliance-detail/${id}`),

  getRegulationSources: (country: string, status?: string) => {
    const params = status ? `?status=${status}` : "";
    return fetchJSON<any[]>(`/regulation-sources/${country}${params}`);
  },

  // Knowledge Base
  searchKnowledge: (query: string, opts: { scope?: string; countryCode?: string; knowledgeBaseIds?: string[]; topK?: number }) =>
    fetchJSON<{ results: any[]; count: number }>("/knowledge/search", {
      method: "POST",
      body: JSON.stringify({
        query,
        scope: opts.scope,
        country_code: opts.countryCode,
        knowledge_base_ids: opts.knowledgeBaseIds,
        top_k: opts.topK ?? 5,
      }),
    }),

  // Feedback
  submitFeedback: (data: { entity_type: string; entity_id: string; rating: "up" | "down"; comment?: string; context?: Record<string, string> }) =>
    fetchJSON<{ ok: boolean }>("/feedback", { method: "POST", body: JSON.stringify(data) }),

  getMyFeedback: () =>
    fetchJSON<Array<{ entity_type: string; entity_id: string; rating: string; comment: string | null }>>("/feedback/my"),

  // Cloud Drive
  // Guided Tasks
  getGuidedTasks: (projectId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/tasks`),

  // License Status + Submissions
  updateLicenseStatus: (projectId: string, licenseTypeId: string, data: {
    status: string; submitted_date?: string; decision_date?: string;
    expiry_date?: string; license_number?: string; notes?: string;
  }) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/licenses/${licenseTypeId}/status`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  getLicenseSubmissions: (projectId: string, licenseTypeId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/licenses/${licenseTypeId}/submissions`),

  addLicenseSubmission: (projectId: string, licenseTypeId: string, data: {
    event_type: string; event_date: string; reference_number?: string;
    regulator_contact?: string; channel?: string; description?: string;
    follow_up_date?: string;
  }) =>
    fetchJSON<{ id: string; ok: boolean }>(`/projects/${projectId}/licenses/${licenseTypeId}/submissions`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getCloudStatus: (projectId: string) =>
    fetchJSON<any>(`/projects/${projectId}/cloud/status`),

  startCloudConnect: (projectId: string, provider: string) =>
    fetchJSON<{ authUrl: string }>(`/projects/${projectId}/cloud/connect`, {
      method: "POST",
      body: JSON.stringify({ provider }),
    }),

  disconnectCloud: (projectId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/cloud/disconnect`, { method: "POST" }),

  triggerCloudSync: (projectId: string) =>
    fetchJSON<any>(`/projects/${projectId}/cloud/sync`, { method: "POST" }),

  getCloudFolders: (projectId: string, parentId?: string) => {
    const params = parentId ? `?parent=${parentId}` : "";
    return fetchJSON<{ folders: any[]; parentId: string; parentName: string }>(`/projects/${projectId}/cloud/folders${params}`);
  },

  selectCloudFolder: (projectId: string, folderId: string, folderName: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/cloud/select-folder`, {
      method: "POST",
      body: JSON.stringify({ folderId, folderName }),
    }),

  getCloudReviewQueue: (projectId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/cloud/review`),

  resolveCloudReview: (projectId: string, reviewId: string, action: "approve" | "dismiss", docType?: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/cloud/review/${reviewId}`, {
      method: "PATCH",
      body: JSON.stringify({ action, doc_type: docType }),
    }),

  getFeedbackSummary: () =>
    fetchJSON<{ feedback: any[]; interview_count: number; interviews_by_country: any[] }>("/admin/feedback-summary"),

  getAdminKpis: (passphrase: string) => {
    const token = getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Admin-Passphrase": passphrase,
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${BASE}/admin/kpis`, { headers }).then(res => {
      if (!res.ok) throw new Error(res.status === 403 ? "Invalid passphrase" : `API error: ${res.status}`);
      return res.json();
    });
  },
};

// Public API (no auth required)
export interface CheckupSnapshot {
  country: string;
  capacity_kw: number;
  intent: string;
  fields: Record<string, string>;
  mapped_fields: Record<string, string>;
}

export const checkupSnapshot = {
  // The token is REQUIRED — the server resolves ONLY by it (a tokenless call is a guaranteed
  // no-op there), so the compiler should refuse a call site that hasn't got one. It rides in
  // from the page URL (user-editable), so encode it: a crafted value must stay one query
  // param, not become several.
  get: (token: string) => fetchJSON<{ snapshot: CheckupSnapshot | null }>(
    `/checkup-snapshot?token=${encodeURIComponent(token)}`
  ),
  clear: (token: string) => fetchJSON<{ ok: boolean }>(
    `/checkup-snapshot?token=${encodeURIComponent(token)}`,
    { method: "POST" }
  ),
};

export async function getCheckup(data: {
  country_code: string;
  capacity_kw: number;
  fields: Record<string, string>;
  intent?: string;
}) {
  const res = await fetch(`${BASE}/checkup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `Checkup failed: ${res.status}`);
  }
  return res.json();
}

/** Attach an email to a server-side checkup completion, for lead follow-up. Best-effort:
 *  the token is already the signup credential without it, so a failure here never blocks. */
/**
 * Record a lead-capture / demand signal on the waitlist — via the server, never straight to
 * Supabase (rate-gated, validated, length-capped there; see lib/waitlist-server.ts).
 * Resolves true on success; false means "tell the human it didn't record" where that matters.
 */
export async function recordWaitlist(signal: {
  source: "checkup" | "regulatory_request" | "login";
  email: string;
  country: string;
  capacity?: string;
  note?: string;
  name?: string;
  company?: string;
  checkup_snapshot?: Record<string, unknown>;
  /** Checkup source only: the server-minted completion token — the snapshot's pre-fill join key. */
  checkup_token?: string;
}): Promise<boolean> {
  const res = await fetch(`${BASE}/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(signal),
  });
  return res.ok;
}

export async function attachCheckupEmail(token: string, email: string) {
  const res = await fetch(`${BASE}/checkup/email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, email }),
  });
  return res.ok;
}
